import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ReloadOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  SendOutlined,
  ArrowLeftOutlined,
  UserOutlined,
} from '@ant-design/icons';
import aiAvatarImg from '@/assets/images/ip-logo100X100.png';
import { Select, InputNumber, Button, Input, Dropdown, message } from 'antd';
import type { MenuProps } from 'antd';
import {
  getOrganizationList,
  getOntologyTree,
  getKnowledgeBaseList,
  type OntologyNode,
} from '../../../../api';
import type { ChatMessage, RunConfig } from '../../index';
import PickerField from './PickerField';
import OntologyPickerModal, {
  type OntologySelection,
} from './OntologyPickerModal';
import KnowledgeBasePickerModal, {
  type KnowledgeBaseSelection,
} from './KnowledgeBasePickerModal';
import {
  MOCK_PRESETS,
  getConfiguredMockPreset,
  type MockPresetKey,
} from './mockPresets';

/** 变量项 */
interface VariableItem {
  key: string;
  /** 变量类型：number 走数值输入，string 走文本输入 */
  type: 'number' | 'string';
  value: string | number | null;
}

/**
 * 从本体树中按 ontologyId 递归查找节点，返回 name + 路径
 * 找不到则返回 undefined
 */
function findOntologyById(
  nodes: OntologyNode[],
  ontologyId: number,
  pathNames: string[] = []
): { ontologyId: number; name: string; pathNames: string[] } | undefined {
  for (const node of nodes) {
    if (node.type === 'ONTOLOGY' && node.ontologyId === ontologyId) {
      return { ontologyId: node.ontologyId, name: node.name, pathNames };
    }
    if (node.children?.length) {
      const found = findOntologyById(node.children, ontologyId, [
        ...pathNames,
        node.name,
      ]);
      if (found) return found;
    }
  }
  return undefined;
}

function findOntologyByName(
  nodes: OntologyNode[],
  ontologyName: string,
  pathNames: string[] = []
): { ontologyId: number; name: string; pathNames: string[] } | undefined {
  for (const node of nodes) {
    if (
      node.type === 'ONTOLOGY' &&
      node.name === ontologyName &&
      node.ontologyId !== undefined
    ) {
      return { ontologyId: node.ontologyId, name: node.name, pathNames };
    }
    if (node.children?.length) {
      const found = findOntologyByName(node.children, ontologyName, [
        ...pathNames,
        node.name,
      ]);
      if (found) return found;
    }
  }
  return undefined;
}

function isValidPresetName(value?: string | null): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function isValidPresetId(value?: number | null): value is number {
  return typeof value === 'number';
}

interface Props {
  messages: ChatMessage[];
  onStartRun: (question: string, config: RunConfig) => void;
  /** 最近一次执行完成后，允许从对话回看过程图 */
  canViewProcess?: boolean;
  /** 切换回最近一次执行过程视图 */
  onViewProcess?: () => void;
  onBackToList?: () => void;
}

function formatJsonScalar(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  return JSON.stringify(value);
}

/** 结构化 JSON 结果展示：兼容对象、数组与基础类型。 */
const JsonAnswerView: React.FC<{ value: unknown }> = ({ value }) => {
  if (Array.isArray(value)) {
    return (
      <div className="space-y-3">
        {value.map((item, index) => (
          <div
            key={index}
            className="rounded-xl border border-solid border-[#dbe7f3] bg-[#f8fbff] px-4 py-3"
          >
            <JsonAnswerView value={item} />
          </div>
        ))}
      </div>
    );
  }

  if (value && typeof value === 'object') {
    return (
      <div className="space-y-2">
        {Object.entries(value).map(([key, fieldValue]) => (
          <div
            key={key}
            className="overflow-hidden rounded-lg border border-solid border-[#e5edf5] bg-white"
          >
            <div className="border-0 border-b border-solid border-[#eef3f8] bg-[#f7fafc] px-3 py-2 text-xs font-semibold text-[#5b6b7b]">
              {key}
            </div>
            <div className="px-3 py-2 text-sm leading-6 text-[#374151]">
              {fieldValue !== null && typeof fieldValue === 'object' ? (
                <JsonAnswerView value={fieldValue} />
              ) : (
                <div className="whitespace-pre-wrap break-words">
                  {formatJsonScalar(fieldValue)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="whitespace-pre-wrap break-words text-sm leading-6 text-[#374151]">
      {formatJsonScalar(value)}
    </div>
  );
};

const ConfigAndSendQuestion: React.FC<Props> = ({
  messages,
  onStartRun,
  canViewProcess = false,
  onViewProcess,
  onBackToList,
}) => {
  // 左侧表单状态
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(undefined);
  const [selectedScenarioOntology, setSelectedScenarioOntology] = useState<
    number | undefined
  >(undefined);
  const [selectedScenarioOntologyName, setSelectedScenarioOntologyName] =
    useState<string | undefined>(undefined);
  const [selectedObjectOntology, setSelectedObjectOntology] = useState<
    number | undefined
  >(undefined);
  const [selectedObjectOntologyName, setSelectedObjectOntologyName] = useState<
    string | undefined
  >(undefined);
  const [selectedKnowledge, setSelectedKnowledge] = useState<
    string | undefined
  >(undefined);
  /** 知识库展示名称 */
  const [selectedKnowledgeName, setSelectedKnowledgeName] = useState<
    string | undefined
  >(undefined);
  const [variables, setVariables] = useState<VariableItem[]>([
    { key: '', type: 'number', value: null },
  ]);

  // 弹窗开关
  const [scenarioOntologyModalOpen, setScenarioOntologyModalOpen] =
    useState(false);
  const [objectOntologyModalOpen, setObjectOntologyModalOpen] = useState(false);
  const [knowledgeModalOpen, setKnowledgeModalOpen] = useState(false);

  // 右侧输入状态
  const [question, setQuestion] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  // 获取组织列表（仅启用状态）：与组织管理列表分缓存，避免互相影响。
  const { data: orgList = [] } = useQuery({
    queryKey: ['organization-list', { status: 'enabled' }],
    queryFn: () => getOrganizationList({ status: 'enabled' }),
  });

  /**
   * 预加载本体树与知识库列表（与 Modal 内部共用 queryKey 走缓存）
   * 目的：在“填入模拟数据”时能反查 name；Modal 打开时可直接命中缓存。
   */
  const { data: ontologyTree = [] } = useQuery({
    queryKey: ['ontology-tree'],
    queryFn: getOntologyTree,
  });
  const { data: knowledgeList = [] } = useQuery({
    queryKey: ['knowledge-base-list'],
    queryFn: getKnowledgeBaseList,
  });

  /** 添加变量 */
  const addVariable = () => {
    setVariables(prev => [...prev, { key: '', type: 'number', value: null }]);
  };

  /** 删除变量 */
  const removeVariable = (index: number) => {
    setVariables(prev => prev.filter((_, i) => i !== index));
  };

  /** 更新变量 */
  const updateVariable = (
    index: number,
    field: 'key' | 'value',
    val: string | number | null
  ) => {
    setVariables(prev =>
      prev.map((item, i) => (i === index ? { ...item, [field]: val } : item))
    );
  };

  /** 切换变量类型：尽量保留已有值并做类型转换，避免用户误操作导致已填内容被清空 */
  const updateVariableType = (index: number, type: 'number' | 'string') => {
    setVariables(prev =>
      prev.map((item, i) => {
        if (i !== index) return item;
        let nextValue: string | number | null = item.value;
        if (type === 'string') {
          // 目标类型为字符串：数值转字符串，null 置为空串
          if (typeof item.value === 'number') nextValue = String(item.value);
          else if (item.value === null) nextValue = '';
        } else {
          // 目标类型为数值：字符串尝试转为数字，非法则置为 null
          if (typeof item.value === 'string') {
            const trimmed = item.value.trim();
            const n = Number(trimmed);
            nextValue = trimmed !== '' && Number.isFinite(n) ? n : null;
          }
        }
        return { ...item, type, value: nextValue };
      })
    );
  };

  /** 重置表单 */
  const handleReset = () => {
    setSelectedOrg(undefined);
    setSelectedScenarioOntology(undefined);
    setSelectedScenarioOntologyName(undefined);
    setSelectedObjectOntology(undefined);
    setSelectedObjectOntologyName(undefined);
    setSelectedKnowledge(undefined);
    setSelectedKnowledgeName(undefined);
    setVariables([{ key: '', type: 'number', value: null }]);
  };

  /** 按预设填入模拟数据，方便调试。 */
  const applyMockPreset = (presetKey: MockPresetKey) => {
    const preset = getConfiguredMockPreset(presetKey);
    if (!preset) return;

    // 选择组织列表中的第一个组织
    if (orgList.length > 0) {
      setSelectedOrg(orgList[0].id);
    }

    const matchedScenarioOntology = isValidPresetName(
      preset.scenarioOntologyName
    )
      ? findOntologyByName(ontologyTree, preset.scenarioOntologyName)
      : isValidPresetId(preset.scenarioOntologyId)
        ? findOntologyById(ontologyTree, preset.scenarioOntologyId)
        : undefined;
    if (matchedScenarioOntology) {
      setSelectedScenarioOntology(matchedScenarioOntology.ontologyId);
      setSelectedScenarioOntologyName(matchedScenarioOntology.name);
    } else if (isValidPresetName(preset.scenarioOntologyName)) {
      setSelectedScenarioOntology(undefined);
      setSelectedScenarioOntologyName(preset.scenarioOntologyName);
    } else if (isValidPresetId(preset.scenarioOntologyId)) {
      setSelectedScenarioOntology(preset.scenarioOntologyId);
      setSelectedScenarioOntologyName(`场景本体 #${preset.scenarioOntologyId}`);
    } else {
      setSelectedScenarioOntology(undefined);
      setSelectedScenarioOntologyName(undefined);
    }

    const matchedObjectOntology = isValidPresetName(preset.objectOntologyName)
      ? findOntologyByName(ontologyTree, preset.objectOntologyName)
      : isValidPresetId(preset.objectOntologyId)
        ? findOntologyById(ontologyTree, preset.objectOntologyId)
        : undefined;
    if (matchedObjectOntology) {
      setSelectedObjectOntology(matchedObjectOntology.ontologyId);
      setSelectedObjectOntologyName(matchedObjectOntology.name);
    } else if (isValidPresetName(preset.objectOntologyName)) {
      setSelectedObjectOntology(undefined);
      setSelectedObjectOntologyName(preset.objectOntologyName);
    } else if (isValidPresetId(preset.objectOntologyId)) {
      setSelectedObjectOntology(preset.objectOntologyId);
      setSelectedObjectOntologyName(`对象本体 #${preset.objectOntologyId}`);
    } else {
      setSelectedObjectOntology(undefined);
      setSelectedObjectOntologyName(undefined);
    }

    const matchedKnowledge = isValidPresetName(preset.knowledgeBaseName)
      ? knowledgeList.find(k => k.name === preset.knowledgeBaseName)
      : isValidPresetName(preset.knowledgeBaseId)
        ? knowledgeList.find(k => k.id === preset.knowledgeBaseId)
        : undefined;
    if (matchedKnowledge) {
      setSelectedKnowledge(matchedKnowledge.id);
      setSelectedKnowledgeName(matchedKnowledge.name);
    } else if (isValidPresetName(preset.knowledgeBaseName)) {
      setSelectedKnowledge(undefined);
      setSelectedKnowledgeName(preset.knowledgeBaseName);
    } else if (isValidPresetName(preset.knowledgeBaseId)) {
      setSelectedKnowledge(preset.knowledgeBaseId);
      setSelectedKnowledgeName(preset.knowledgeBaseId);
    } else {
      setSelectedKnowledge(undefined);
      setSelectedKnowledgeName(undefined);
    }

    setVariables([...preset.variables]);
    setQuestion(preset.question);
  };

  /** 点击“填入模拟数据”后展示下拉选项，让用户明确选择具体预设。 */
  const mockMenuItems: MenuProps['items'] = MOCK_PRESETS.map(item => ({
    key: item.key,
    label: item.label,
  }));

  const [messageApi, contextHolder] = message.useMessage();
  /** 只在最后一条 AI 回复末尾展示“查看执行过程”，避免历史多轮对话入口混淆。 */
  const lastAssistantMessageIndex = messages.reduce(
    (lastIndex, msg, index) => (msg.role === 'assistant' ? index : lastIndex),
    -1
  );

  /** 发送问题：校验必填项 + 收集完整配置 */
  const handleSend = () => {
    const trimmed = question.trim();
    if (!trimmed) {
      messageApi.warning('请输入问题');
      return;
    }
    // 必填校验：组织
    if (!selectedOrg) {
      messageApi.warning('请选择组织');
      return;
    }
    if (!selectedScenarioOntology) {
      messageApi.warning('请选择场景');
      return;
    }
    const config: RunConfig = {
      orgId: selectedOrg,
      orgName: orgList.find(o => o.id === selectedOrg)?.name,
      scenarioId: selectedScenarioOntology,
      scenarioName: selectedScenarioOntologyName,
      objectOntologyId: selectedObjectOntology,
      objectOntologyName: selectedObjectOntologyName,
      knowledgeBaseId: selectedKnowledge,
      knowledgeBaseName: selectedKnowledgeName,
      processJson: '',
      variables: variables.filter(v => v.key.trim()),
    };
    onStartRun(trimmed, config);
    setQuestion('');
  };

  /** 键盘事件：Enter 发送，Shift+Enter 换行 */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="relative flex h-full min-h-0">
      {contextHolder}
      {/* ========== 左侧：运行配置 ========== */}
      <div className="w-[380px] shrink-0 overflow-y-auto border-0 border-r border-solid border-[#d1d5db] bg-white px-5 py-5">
        {/* 标题行 */}
        <div className="mb-0 flex items-center justify-between">
          <h3 className="text-base font-bold text-[#111827]">运行配置：</h3>
          <div className="flex items-center gap-2">
            <Dropdown
              trigger={['click']}
              menu={{
                items: mockMenuItems,
                onClick: ({ key }) => applyMockPreset(key as MockPresetKey),
              }}
            >
              <button
                type="button"
                className="cursor-pointer border-none bg-transparent p-0 text-xs text-[#2094f0] transition-colors hover:text-[#1a7fd4]"
              >
                填入模拟数据
              </button>
            </Dropdown>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleReset}
              className="!rounded-md !border-[#d1d5db] !text-xs"
            >
              重置
            </Button>
          </div>
        </div>

        {/* 选择组织 */}
        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-medium text-[#374151]">
            选择组织 <span className="text-[#ef4444]">*</span>
          </label>
          <Select
            value={selectedOrg}
            onChange={setSelectedOrg}
            placeholder="请选择组织"
            className="w-full"
            options={orgList.map(org => ({
              label: org.name,
              value: org.id,
            }))}
          />
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-medium text-[#374151]">
            选择场景 <span className="text-[#ef4444]">*</span>
          </label>
          <PickerField
            value={selectedScenarioOntologyName}
            placeholder="请选择场景"
            onClick={() => setScenarioOntologyModalOpen(true)}
            onClear={() => {
              setSelectedScenarioOntology(undefined);
              setSelectedScenarioOntologyName(undefined);
            }}
          />
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-medium text-[#374151]">
            选择对象本体
          </label>
          <PickerField
            value={selectedObjectOntologyName}
            placeholder="请选择对象本体"
            onClick={() => setObjectOntologyModalOpen(true)}
            onClear={() => {
              setSelectedObjectOntology(undefined);
              setSelectedObjectOntologyName(undefined);
            }}
          />
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-medium text-[#374151]">
            选择知识库
          </label>
          <PickerField
            value={selectedKnowledgeName}
            placeholder="请选择知识库"
            onClick={() => setKnowledgeModalOpen(true)}
            onClear={() => {
              setSelectedKnowledge(undefined);
              setSelectedKnowledgeName(undefined);
            }}
          />
        </div>

        <OntologyPickerModal
          open={scenarioOntologyModalOpen}
          value={selectedScenarioOntology}
          onCancel={() => setScenarioOntologyModalOpen(false)}
          onOk={(sel: OntologySelection) => {
            setSelectedScenarioOntology(sel.ontologyId);
            setSelectedScenarioOntologyName(sel.name);
            setScenarioOntologyModalOpen(false);
          }}
        />

        <OntologyPickerModal
          open={objectOntologyModalOpen}
          value={selectedObjectOntology}
          onCancel={() => setObjectOntologyModalOpen(false)}
          onOk={(sel: OntologySelection) => {
            setSelectedObjectOntology(sel.ontologyId);
            setSelectedObjectOntologyName(sel.name);
            setObjectOntologyModalOpen(false);
          }}
        />

        {/* 知识库选择弹窗 */}
        <KnowledgeBasePickerModal
          open={knowledgeModalOpen}
          value={selectedKnowledge}
          onCancel={() => setKnowledgeModalOpen(false)}
          onOk={(sel: KnowledgeBaseSelection) => {
            setSelectedKnowledge(sel.id);
            setSelectedKnowledgeName(sel.name);
            setKnowledgeModalOpen(false);
          }}
        />

        {/* 变量 */}
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-xs font-medium text-[#374151]">变量</span>
            <button
              type="button"
              onClick={addVariable}
              className="flex cursor-pointer items-center justify-center border-none bg-transparent p-0 text-[#2094f0] transition-colors hover:text-[#1a7fd4]"
            >
              <PlusCircleOutlined style={{ fontSize: 16 }} />
            </button>
          </div>

          <div className="space-y-2">
            {variables.map((variable, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  value={variable.key}
                  onChange={e => updateVariable(index, 'key', e.target.value)}
                  placeholder="输入变量名"
                  className="!h-8 w-[90px] flex-1 !rounded-md !text-xs"
                />
                <Select
                  value={variable.type}
                  onChange={t => updateVariableType(index, t)}
                  className="!h-8 w-[96px] !text-xs"
                  options={[
                    { label: '数值', value: 'number' },
                    { label: '字符串', value: 'string' },
                  ]}
                />
                {variable.type === 'number' ? (
                  <InputNumber
                    value={
                      typeof variable.value === 'number' ? variable.value : null
                    }
                    onChange={val => updateVariable(index, 'value', val)}
                    placeholder="输入变量值"
                    className="!h-8 w-[90px] !rounded-md !text-xs"
                  />
                ) : (
                  <Input
                    value={
                      typeof variable.value === 'string' ? variable.value : ''
                    }
                    onChange={e =>
                      updateVariable(index, 'value', e.target.value)
                    }
                    placeholder="输入变量值"
                    className="!h-8 w-[90px] !rounded-md !text-xs"
                  />
                )}
                <button
                  type="button"
                  onClick={() => removeVariable(index)}
                  className="flex cursor-pointer items-center justify-center border-none bg-transparent p-0 text-[#ef4444] transition-colors hover:text-[#dc2626]"
                >
                  <MinusCircleOutlined style={{ fontSize: 16 }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ========== 右侧：问题输入 & 对话区 ========== */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {onBackToList && (
          <div className="pointer-events-none absolute left-6 top-5 z-10">
            <button
              type="button"
              onClick={onBackToList}
              className="pointer-events-auto flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-[#d0d5dd] bg-white text-[#344054] transition-colors hover:border-[#98a2b3] hover:text-[#101828]"
              aria-label="返回任务列表"
            >
              <ArrowLeftOutlined />
            </button>
          </div>
        )}
        {/* 对话内容区域 */}
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {messages.length === 0 ? (
            // 空白占位
            <div className="flex h-full items-center justify-center">
              <p className="text-sm text-[#d1d5db]" />
            </div>
          ) : (
            <div className="mx-auto max-w-[800px] space-y-6">
              {messages.map((msg, idx) => (
                <div key={idx}>
                  {msg.role === 'user' ? (
                    // 用户消息
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#2094f0] text-white">
                        <UserOutlined
                          style={{
                            fontSize: 13,
                            transform: 'translate(-0.15px, -1px)',
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm my-0 font-medium leading-snug text-[#111827]">
                          {msg.content}
                        </p>
                        {msg.time && (
                          <p className="mt-1 text-xs text-[#9ca3af]">
                            {msg.time}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    // AI 回复
                    <div className="flex items-start gap-3">
                      <img
                        src={aiAvatarImg}
                        alt="AI"
                        className="mt-0.5 h-7 w-7 shrink-0 rounded-full object-contain"
                      />
                      <div className="flex-1">
                        {msg.displayMode === 'json' ? (
                          <JsonAnswerView value={msg.structuredContent} />
                        ) : (
                          <p className="my-0 whitespace-pre-wrap text-sm leading-snug text-[#374151]">
                            {msg.content}
                          </p>
                        )}
                        {canViewProcess &&
                          idx === lastAssistantMessageIndex && (
                            <button
                              type="button"
                              onClick={onViewProcess}
                              className="mt-2 cursor-pointer border-none bg-transparent p-0 text-xs font-medium text-[#2094f0] transition-colors hover:text-[#1a7fd4]"
                            >
                              查看执行过程
                            </button>
                          )}
                        {msg.time && (
                          <p className="mt-1 text-xs text-[#9ca3af]">
                            {msg.time}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 底部输入区域 */}
        <div className="border-t border-[#e5e7eb] px-6 py-4">
          <div className="mx-auto max-w-[800px]">
            <div
              className="flex flex-col gap-2 rounded-[20px] bg-white px-5 py-3 transition-all"
              style={{
                border: inputFocused
                  ? '1px solid #129bff'
                  : '1px solid #d9d9d9',
                boxShadow: inputFocused
                  ? '0 15px 13px -4px rgba(18,155,255,0.1)'
                  : 'none',
              }}
            >
              <textarea
                value={question}
                onChange={e => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="请输入问题..."
                rows={4}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                className="flex-1 resize-none border-none bg-transparent text-sm text-[#111827] outline-none placeholder:text-[#9ca3af]"
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!question.trim()}
                  className="flex h-9 w-[100px] shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-lg border-none bg-[#2094f0] text-xs font-medium text-white transition-colors hover:bg-[#1a7fd4] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <SendOutlined />
                  开始执行
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigAndSendQuestion;
